import * as React from "react";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export type WorkspaceDrawerActionItem = {
  id: string;
  label: string;
  disabled?: boolean;
  /** Opens in a new tab when set (e.g. Need Help). */
  href?: string;
  onSelect?: () => void;
  /** Leading icon (16px recommended; component enforces size-4). */
  icon?: React.ReactNode;
  /**
   * Matches workspace node action menus:
   * - default → gray-500
   * - brand → tropical surf (e.g. View Active Pinescript)
   * - warning → glacial orange (archive / caution)
   * - destructive → glacial red
   */
  tone?: "default" | "destructive" | "brand" | "orange";
  separatorBefore?: boolean;
};

export type WorkspaceDrawerActionsMenuProps = {
  items: WorkspaceDrawerActionItem[];
  /** Disable the Actions trigger. */
  disabled?: boolean;
  className?: string;
  align?: "start" | "center" | "end";
};

/** Same row chrome as `WorkspaceNodeActions` under-node menus. */
const itemBaseClass = cn(
  "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
  "font-[family-name:var(--font-primary)] font-normal",
  "data-disabled:pointer-events-none data-disabled:opacity-50",
  "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
);

function itemToneClass(tone: WorkspaceDrawerActionItem["tone"]): string {
  if (tone === "destructive") {
    return cn(
      itemBaseClass,
      "text-[color:var(--brand-glacial-red)]",
      "focus:bg-[color:var(--brand-glacial-red)]/10 focus:text-[color:var(--brand-glacial-red)]",
      "data-[highlighted]:bg-[color:var(--brand-glacial-red)]/10 data-[highlighted]:text-[color:var(--brand-glacial-red)]"
    );
  }
  if (tone === "orange") {
    return cn(
      itemBaseClass,
      "text-[color:var(--brand-comparison-period)]",
      "focus:bg-[color:var(--brand-comparison-period)]/10 focus:text-[color:var(--brand-comparison-period)]",
      "data-[highlighted]:bg-[color:var(--brand-comparison-period)]/10 data-[highlighted]:text-[color:var(--brand-comparison-period)]"
    );
  }
  if (tone === "brand") {
    return cn(
      itemBaseClass,
      "text-[color:var(--brand-tropical-surf)]",
      "focus:bg-[color:var(--brand-tropical-surf)]/10 focus:text-[color:var(--brand-tropical-surf)]",
      "data-[highlighted]:bg-[color:var(--brand-tropical-surf)]/10 data-[highlighted]:text-[color:var(--brand-tropical-surf)]"
    );
  }
  return cn(
    itemBaseClass,
    "text-[#6b7280] dark:text-white",
    "focus:bg-accent focus:text-[#6b7280] dark:focus:text-white",
    "data-[highlighted]:bg-accent data-[highlighted]:text-[#6b7280] dark:data-[highlighted]:text-white"
  );
}

function itemDataVariant(
  tone: WorkspaceDrawerActionItem["tone"]
): "destructive" | "warning" | "brand" | undefined {
  if (tone === "destructive") return "destructive";
  if (tone === "orange") return "warning";
  if (tone === "brand") return "brand";
  return undefined;
}

function ActionRowContent({
  icon,
  label,
}: {
  icon?: React.ReactNode;
  label: string;
}): React.ReactElement {
  return (
    <>
      {icon ? (
        <span className="inline-flex size-4 shrink-0 items-center justify-center [&_svg]:size-4">
          {icon}
        </span>
      ) : null}
      <span className="whitespace-nowrap">{label}</span>
    </>
  );
}

/**
 * Single “Actions” dropdown for workspace node sheets — styled like under-node menus.
 */
export function WorkspaceDrawerActionsMenu({
  items,
  disabled = false,
  className,
  align = "start",
}: WorkspaceDrawerActionsMenuProps): React.ReactElement | null {
  const visibleItems = items.filter(Boolean);
  if (visibleItems.length === 0) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline-gray"
          size="sm"
          data-font="primary"
          data-workspace-drawer-actions=""
          className={cn("h-9 shrink-0 gap-1.5", className)}
          disabled={disabled}
          aria-label="Actions"
        >
          <span className="normal-case tracking-normal">Actions</span>
          <ChevronDown className="size-4 shrink-0 opacity-80" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        sideOffset={6}
        // Reuse node action-menu visual system (`data-workspace-node-actions` CSS + class chrome).
        data-workspace-node-actions=""
        data-workspace-drawer-actions-menu=""
        className={cn(
          "z-[1200] w-max min-w-[12.5rem] overflow-visible rounded-[6px] bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10",
          "font-[family-name:var(--font-primary)]"
        )}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        {visibleItems.map((item) => {
          const tone = item.tone ?? "default";
          const dataVariant = itemDataVariant(tone);
          const classNameForItem = itemToneClass(tone);

          return (
            <React.Fragment key={item.id}>
              {item.separatorBefore ? (
                <DropdownMenuSeparator className="-mx-1 my-1 h-px bg-border" />
              ) : null}
              {item.href ? (
                <DropdownMenuItem
                  asChild
                  disabled={item.disabled}
                  data-variant={dataVariant}
                  className={cn(classNameForItem, "cursor-pointer")}
                >
                  <a href={item.href} target="_blank" rel="noopener noreferrer">
                    <ActionRowContent icon={item.icon} label={item.label} />
                  </a>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  disabled={item.disabled}
                  variant={tone === "destructive" ? "destructive" : "default"}
                  data-variant={dataVariant}
                  className={cn(classNameForItem, "cursor-pointer")}
                  onSelect={() => {
                    item.onSelect?.();
                  }}
                >
                  <ActionRowContent icon={item.icon} label={item.label} />
                </DropdownMenuItem>
              )}
            </React.Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
